import { surfaceAt, type Level } from "./level";

// Things to pick up in a 2-minute run (THPS career style): the S-K-A-T-E
// letters, the C-O-M-B-O letters (all five in one combo) and the Secret Tape.
// A map file places them by `x, z` and a height `h` above the park surface
// there; `placeCollectables` resolves that to world positions. Sim-free: the
// run checks the skater's position against them each step.

/** A pickup's spot in a map file: `h` metres above the surface at (x, z). */
export interface CollectableSpot {
  x: number;
  z: number;
  h: number;
}

export type CollectableKind = "skate" | "combo" | "tape";

export interface Collectable {
  /** "skate-0".."skate-4", "combo-0".."combo-4", "tape". */
  id: string;
  kind: CollectableKind;
  /** The letter it shows ("S", "C"...), or "" for the tape. */
  letter: string;
  x: number;
  y: number;
  z: number;
}

export const SKATE_LETTERS = ["S", "K", "A", "T", "E"] as const;
export const COMBO_LETTERS = ["C", "O", "M", "B", "O"] as const;

/** Lowest and highest a pickup may float above the surface (the top is about a big ollie). */
export const SPOT_MIN_H = 0.4;
export const SPOT_MAX_H = 6;

/** Horizontal pickup reach from the skater's centre. */
export const PICKUP_R = 1.1;
/** A pickup counts from a little under the board up to about head height. */
const PICKUP_BELOW = 0.6;
const PICKUP_ABOVE = 2.1;

/** World positions for a map's pickups, in goal order (letters S..E, C..O, then the tape). */
export function placeCollectables(
  spots: { letters?: CollectableSpot[]; combo?: CollectableSpot[]; tape?: CollectableSpot },
  level: Level,
): Collectable[] {
  const at = (id: string, kind: CollectableKind, letter: string, s: CollectableSpot): Collectable => ({
    id,
    kind,
    letter,
    x: s.x,
    y: surfaceAt(level, s.x, s.z).h + s.h,
    z: s.z,
  });
  const out: Collectable[] = [];
  spots.letters?.forEach((s, i) => out.push(at(`skate-${i}`, "skate", SKATE_LETTERS[i]!, s)));
  spots.combo?.forEach((s, i) => out.push(at(`combo-${i}`, "combo", COMBO_LETTERS[i]!, s)));
  if (spots.tape) out.push(at("tape", "tape", "", spots.tape));
  return out;
}

/** Whether a skater standing on the board at (x, y, z) picks `c` up. */
export function touches(c: Collectable, x: number, y: number, z: number): boolean {
  const dy = c.y - y;
  return dy >= -PICKUP_BELOW && dy <= PICKUP_ABOVE && Math.hypot(c.x - x, c.z - z) <= PICKUP_R;
}
